import './styles.modules.scss';
import { FiCheck } from '@react-icons/all-files/fi/FiCheck';
import { FiHeart } from '@react-icons/all-files/fi/FiHeart';
import { Form, Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import * as Yup from 'yup';
import FavoriteListDialog from '../FavoriteListDialog';
import InventoryModal from '../InventoryModal';
import SaleProductAlert from '../SaleProductAlert';
import AddToCart from './AddToCart';
import OptionSelector from './OptionSelector';
import OrderTable from './OrderTable';
import OrderTableCol from './OrderTable/OrderTableCol';
import SingleRow from './SingleRow';
import SubscribeButton from './SubscribeButton';
import TypeRows from './TypeRows';
import { useCart } from './hooks';
import { OptionRecord } from './types';
import { RETURNABLE_PRICE_LIMIT } from '~constants/trade';
import { Product } from '~domain/product/Product';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import { ProductType } from '~redux/brand/types';
import { buyerInfoSelector } from '~redux/buyer/selectors';
import { makeSelectIsSavedByProduct } from '~redux/favorite/reducers/product';
import { makeSelectIsOutOfStockByProduct } from '~redux/inventory';
import { Product as TProduct } from '~types/api';
import { formatCurrency } from '~utils/formatter';
import {
  useInventory,
  useSelectInventoriesByProduct,
  useSelectInventoryByProduct,
} from '~view/hooks/inventory';

type ProductCartFormProps = {
  product: Product;
  productTypes?: ProductType[];
  onSelectColor?: (color: string) => void;
  onSelectSize?: (size: string) => void;
  onSelectType?: (type: string) => void;
  isDirect: boolean;
  preview?: boolean;
  cart?: boolean;
  onSubmit?: () => void;
};

const ProductCartForm: React.FC<ProductCartFormProps> = ({
  product,
  productTypes,
  onSelectColor,
  onSelectSize,
  onSelectType,
  isDirect,
  preview,
  cart,
  onSubmit,
}) => {
  //constants
  const auth = useSelector(authUserSelector);
  const buyerInfo = useSelector(buyerInfoSelector);
  const { isLoading: isInventoryLoading } = useInventory();
  const inventories = useSelectInventoriesByProduct(product.id);
  const inventory = useSelectInventoryByProduct(product.id);
  const isOutOfStock = useSelector(makeSelectIsOutOfStockByProduct(product));
  const productOrderLot = useMemo(() => product.product_order_lot ?? 1, [
    product,
  ]);
  const minimum = useMemo(
    () => product.product_minimum_quantity ?? productOrderLot,
    [product, productOrderLot]
  );
  const [quantities, setQuantities] = useState<
    Record<string, number | undefined>
  >({});
  const [selectedColor, setSelectedColor] = useState<string>();
  const [selectedSize, setSelectedSize] = useState<string>();
  const [selectedType, setSelectedType] = useState<string>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sellOnlyDirect = useMemo(
    () => isDirect && product.brand.sell_only_direct,
    [isDirect, product]
  );

  const colors: OptionRecord | undefined = useMemo(() => {
    if (!productTypes?.length) {
      return;
    }
    const colors = productTypes
      .sort((a, b) => ((a.color ?? '') > (b.color ?? '') ? 1 : -1))
      .reduce(
        (prev, type) =>
          !type.color
            ? prev
            : {
                ...prev,
                [type.color]: { imageKey: type.imageKey ?? undefined },
              },
        {} as OptionRecord
      );
    if (!Object.keys(colors).length) {
      return;
    }
    return colors;
  }, [productTypes]);

  const sizes: OptionRecord | undefined = useMemo(() => {
    if (!productTypes?.length) {
      return;
    }
    const sizes = productTypes
      .sort((a, b) => ((a.size ?? '') > (b.size ?? '') ? 1 : -1))
      .reduce(
        (prev, type) =>
          !type.size
            ? prev
            : {
                ...prev,
                [type.size]: { imageKey: type.imageKey ?? undefined },
              },
        {} as OptionRecord
      );
    if (!Object.keys(sizes).length) {
      return;
    }
    return sizes;
  }, [productTypes]);

  const options: OptionRecord | undefined = useMemo(() => {
    if (!productTypes?.length) {
      return;
    }
    const options = productTypes
      .sort((a, b) => ((a.type_name ?? '') > (b.type_name ?? '') ? 1 : -1))
      .reduce(
        (prev, type) =>
          !type.type_name
            ? prev
            : {
                ...prev,
                [type.type_name]: { imageKey: type.imageKey ?? undefined },
              },
        {} as OptionRecord
      );
    if (!Object.keys(options).length) {
      return;
    }
    return options;
  }, [productTypes]);

  const isSaved = useSelector(makeSelectIsSavedByProduct(product.id));

  //callbacks
  const handleSelectColor = useCallback(
    (color) => {
      setSelectedColor(color);
      onSelectColor?.(color);
      document
        .getElementById(`product-type-col-${product.id}-${color}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    },
    [product.id]
  );

  const handleSelectSize = useCallback((size) => {
    setSelectedSize(size);
    onSelectSize?.(size);
  }, []);

  const handleSelectType = useCallback((type) => {
    setSelectedType(type);
    onSelectType?.(type);
  }, []);

  const { addToCart, updateCart, cartQuantities } = useCart(product.id, cart);
  const handleSubmit = useCallback(
    async (values, formik) => {
      if (!auth || !buyerInfo) {
        return;
      }
      try {
        const quantities = !values.color
          ? values.quantities
          : productTypes!
              .filter((type) => type.color === values.color)
              .reduce(
                (prev, type) => ({
                  ...prev,
                  [type.id]: values.quantities[type.id],
                }),
                {}
              );
        if (cart) {
          await updateCart({ product, productTypes, quantities, isDirect });
          return;
        }
        await addToCart({ product, productTypes, quantities, isDirect });
      } finally {
        formik.setSubmitting(false);
        onSubmit && onSubmit();
      }
    },
    [
      product,
      productTypes,
      auth,
      buyerInfo,
      isDirect,
      cart,
      addToCart,
      updateCart,
      onSubmit,
    ]
  );

  //effects
  useEffect(() => {
    if (!productTypes?.length) {
      return;
    }
    if (productTypes[0].color) {
      setSelectedColor(productTypes[0].color);
    }
    if (productTypes[0].type_name) {
      setSelectedType(productTypes[0].type_name);
    }
  }, [productTypes?.length]);

  useEffect(() => {
    if (cart) return;
    if (!productTypes?.length) {
      setQuantities({
        [product.id]: Math.min(
          product.out_of_stock ? 0 : minimum,
          Math.max(
            inventories[product.sku ?? product.id]?.availableInventory ??
              minimum,
            0
          )
        ),
      });
      return;
    }
    const quantities = productTypes.reduce((prev, type) => {
      const inventory = inventories[type.sku ?? type.id];
      return type.published === false || type.suspended
        ? prev
        : {
            ...prev,
            [type.id]: Math.min(
              type.out_of_stock ? 0 : minimum,
              Math.max(inventory?.availableInventory ?? minimum, 0)
            ),
          };
    }, {} as Record<string, number | undefined>);
    setQuantities(quantities);
  }, [cart, minimum, isInventoryLoading]);

  useEffect(() => {
    if (!cart || !cartQuantities) {
      return;
    }
    const quantities = productTypes?.length
      ? productTypes.reduce(
          (prev, type) => ({
            ...prev,
            [type.id]: cartQuantities[type.id] ?? 0,
          }),
          {}
        )
      : { [product.id]: cartQuantities[product.id] ?? 0 };
    setQuantities(quantities);
  }, [product, productTypes, cartQuantities, cart]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        quantities: Yup.object().shape(
          productTypes?.length
            ? productTypes.reduce(
                (prev, type) => ({
                  ...prev,
                  [type.id]: Yup.number()
                    .nullable()
                    .typeError('数値を入力してください。')
                    .min(0, '0以上の数値を入力してください。')
                    .max(
                      type.product?.product_preorder === 1
                        ? 1000
                        : inventories?.[type.sku ?? type.id]?.[
                            cart ? 'inventory' : 'availableInventory'
                          ] ?? 1000,
                      `${
                        inventories?.[type.sku ?? type.id]?.inventory ?? '1,000'
                      }点まで注文可能です。`
                    ),
                }),
                {}
              )
            : {
                [product.id]: Yup.number()
                  .nullable()
                  .typeError('数値を入力してください。')
                  .min(0, '0以上の数値を入力してください。')
                  .max(
                    product?.product_preorder === 1
                      ? 1000
                      : inventories?.[product.sku ?? product.id]?.[
                          cart ? 'inventory' : 'availableInventory'
                        ] ?? 1000,
                    `${
                      inventories?.[product.sku ?? product.id]?.inventory ??
                      '1,000'
                    }点まで注文可能です。`
                  ),
              }
        ),
      }),
    [cart, isInventoryLoading]
  );

  return (
    <Formik
      initialValues={{ quantities }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnMount
      enableReinitialize
    >
      {() => (
        <Form>
          {colors && (
            <OptionSelector
              optionLabel="カラー"
              selectedOption={selectedColor!}
              options={colors}
              onSelect={handleSelectColor}
            />
          )}
          {sizes && !colors && (
            <OptionSelector
              optionLabel="サイズ"
              selectedOption={selectedSize!}
              options={sizes}
              onSelect={handleSelectSize}
            />
          )}
          {options && (
            <OptionSelector
              optionLabel="種類"
              selectedOption={selectedType!}
              options={options}
              onSelect={handleSelectType}
            />
          )}
          {(cart || !isOutOfStock) && (
            <div className="mb-4">
              <div>
                数量
                {productOrderLot !== 1 && (
                  <span>（発注単位：{productOrderLot}）</span>
                )}
              </div>
              {colors && sizes && (
                <OrderTable sizes={Object.keys(sizes)}>
                  {Object.keys(colors).map((color) => (
                    <OrderTableCol
                      key={color}
                      color={color}
                      sizes={Object.keys(sizes)}
                      selectedColor={selectedColor!}
                      onSelectColor={handleSelectColor}
                      productId={product.id}
                      cart={cart}
                      preview={preview}
                      product={product}
                      productTypes={productTypes!}
                    />
                  ))}
                </OrderTable>
              )}
              {colors && !sizes && (
                <TypeRows
                  product={product}
                  types={productTypes!}
                  isCart={cart}
                />
              )}
              {!colors && sizes && (
                <TypeRows
                  product={product}
                  types={productTypes!}
                  isCart={cart}
                />
              )}
              {!colors && !sizes && options && (
                <TypeRows
                  product={product}
                  types={productTypes!}
                  isCart={cart}
                />
              )}
              {!colors && !sizes && !options && (
                <SingleRow product={product} isCart={cart} />
              )}
            </div>
          )}
          {!preview && (
            <div className="mb-4">
              <SaleProductAlert
                productId={product.id}
                className="border p-3 mb-4 rounded"
              />
              {(cart || !isOutOfStock) && (
                <div className="mb-2">
                  <AddToCart cart={cart} />
                </div>
              )}
              <div className="mb-2">
                <SubscribeButton
                  product={(product as unknown) as TProduct}
                  cart={cart}
                />
              </div>
              {!sellOnlyDirect && (
                <div>
                  <FavoriteListDialog
                    product={product}
                    togglerComponent={(props) => (
                      <Button type="button" outline block {...props}>
                        {!isSaved && (
                          <>
                            <FiHeart className="mr-2" />
                            お気に入りに追加
                          </>
                        )}
                        {isSaved && (
                          <>
                            <FiCheck className="mr-2" />
                            お気に入りに追加済み
                          </>
                        )}
                      </Button>
                    )}
                  />
                </div>
              )}
            </div>
          )}
          {!product.isReturnableCategory && (
            <div className="mb-3">
              <small className="product-return-message">
                {product.product_subcategory}は返品できません
              </small>
            </div>
          )}
          {!sellOnlyDirect &&
            product.product_retail_price >= RETURNABLE_PRICE_LIMIT && (
              <div className="mb-3">
                <small className="product-return-message">
                  希望小売価格（上代）が
                  {formatCurrency(RETURNABLE_PRICE_LIMIT)}
                  以上の商品は返品できません
                </small>
              </div>
            )}
          {!!inventory &&
            (!productTypes?.length ? (
              <>
                {inventory < 10 && (
                  <i className="text-muted">残り{inventory}点</i>
                )}
              </>
            ) : (
              <a
                style={{ textDecoration: 'underline' }}
                onClick={() => setIsModalOpen(true)}
              >
                <>
                  {inventory < 10 && (
                    <i className="text-muted">残り{inventory}点</i>
                  )}
                  {inventory >= 10 && <i className="text-muted">在庫あり</i>}
                </>
                <InventoryModal
                  productId={product.id}
                  isOpen={isModalOpen}
                  toggle={() => setIsModalOpen(false)}
                />
              </a>
            ))}
        </Form>
      )}
    </Formik>
  );
};

export default ProductCartForm;
